// src/pages/ETFCalculatorPage.js
//
// v3.5.99.7.15 — Standalone Early Termination Fee calculator at /etf-calculator.
//
// v7.15 changes the standalone flow to start with HubSpot company/deal
// selection. After selection, the form pre-fills with company name +
// suggested contract reference from the deal. PDF generation auto-uploads
// the unsigned letter to the HubSpot deal as a file attachment (solves
// "we keep losing these and having to regenerate them").
//
// v3.5.99.7.19 adds two things:
//   1. Hotfix — every render_pdf payload now passes the full set of
//      etf_letters fields (mrr, termMonths, contractStartDate, etc.) AND a
//      client-generated UUID used as the row id. Prior versions stored
//      blanks in those columns because the payload was missing the data.
//   2. Void & Create New flow — when the rep clicks "Void & Create New"
//      from the ETF Letters list page, that page navigates here with
//      location.state populated. This component reads that state, prefills
//      every field, skips the HubSpot picker (selection comes from the
//      voided letter's stored hubspot_company_id / hubspot_deal_id), and
//      remembers the supersededFrom id + quoteId so the new letter row
//      links back to the voided one.
//
// Form sections (in order):
//   1. HubSpotDealPicker — pick company → pick deal (or skip deal step)
//   2. EtfCalculatorBody (existing) — financial inputs + actions
//
// Differences from the per-quote modal:
//   - No SignWell send action (no Ferrum signatory identity to use as
//     party 1 in two-party flow — that's per-quote-only territory)
//   - No quoteId / activity log scoped to a quote
//   - No SIGNATURE_COMPLETED auto-fill for contract start (manual entry)
//   - HubSpot deal context comes from the picker, not from a saved quote

import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { calculateEtf, buildEtfLetterHTML } from '../lib/etfCalculator';
import { EtfCalculatorBody } from '../components/ETFCalculatorModal';
import HubSpotDealPicker from '../components/HubSpotDealPicker';
import { v3 } from '../lib/styleTokens';
import { Icon } from '../components/v3';

export default function ETFCalculatorPage() {
  const location = useLocation();
  const { profile } = useAuth();

  // v7.19: Void & Create New flow passes location.state with the voided
  // letter's snapshot. When present, we skip the picker and use the stored
  // company/deal context directly.
  const prefillFromVoid = location?.state?.prefillFromVoid || null;

  // v7.15: HubSpot selection state — populated by picker on deal selection
  const [hubSelection, setHubSelection] = useState(() => {
    if (prefillFromVoid) {
      return {
        companyId:    prefillFromVoid.hubspot_company_id || null,
        companyName:  prefillFromVoid.hubspot_company_name || prefillFromVoid.client_name || '',
        companyAddress: prefillFromVoid.client_address || '',
        dealId:       prefillFromVoid.hubspot_deal_id || null,
        dealName:     prefillFromVoid.contract_reference || null,
        contactName:  prefillFromVoid.client_contact_name || '',
        contactEmail: prefillFromVoid.client_contact_email || '',
        quoteNumber:  prefillFromVoid.contract_reference || '',
      };
    }
    return null;
  });

  // v7.19: track supersession + quote linkage so the new row can reference
  // the voided predecessor. Cleared once the rep changes the form materially
  // (we keep it through any number of regenerations of the same successor).
  const [supersededFrom] = useState(prefillFromVoid?.id || null);
  const [carriedQuoteId] = useState(prefillFromVoid?.quote_id || null);

  const [clientName,        setClientName]        = useState(prefillFromVoid?.client_name || '');
  const [clientAddress,     setClientAddress]     = useState(prefillFromVoid?.client_address || '');
  const [contractReference, setContractReference] = useState(prefillFromVoid?.contract_reference || '');
  const [mrr,               setMrr]               = useState(prefillFromVoid?.mrr != null ? String(prefillFromVoid.mrr) : '');
  const [term,              setTerm]              = useState(prefillFromVoid?.term_months || 36);
  const [contractStart,     setContractStart]     = useState(prefillFromVoid?.contract_start_date || '');
  const [terminationDate,   setTerminationDate]   = useState(() => prefillFromVoid?.termination_date || new Date().toISOString().slice(0, 10));

  const [ferrumInfo, setFerrumInfo] = useState({ name: 'Ferrum Technology Services', address: '', phone: '', website: '', logoUrl: '' });
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfStatus,     setPdfStatus]     = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadInfo() {
      const { data, error } = await supabase
        .from('pricing_settings')
        .select('key, value')
        .in('key', ['company_name', 'company_address', 'company_phone', 'company_website', 'company_logo_url']);
      if (cancelled || error) return;
      const map = {};
      for (const r of (data || [])) map[r.key] = r.value;
      setFerrumInfo({
        name:    map.company_name    || 'Ferrum Technology Services',
        address: map.company_address || '',
        phone:   map.company_phone   || '',
        website: map.company_website || '',
        logoUrl: map.company_logo_url || '',
      });
    }
    loadInfo();
    return () => { cancelled = true; };
  }, []);

  function handleHubSelect(selection) {
    setHubSelection(selection);
    if (!selection) {
      setClientName('');
      setClientAddress('');
      setContractReference('');
      return;
    }
    // Only fill when current field is empty — don't blow away rep edits
    setClientName(prev => prev || selection.companyName || '');
    // v7.18.1: pre-fill Client Address from HubSpot company record
    if (selection.companyAddress) setClientAddress(prev => prev || selection.companyAddress);
    if (selection.quoteNumber) setContractReference(prev => prev || selection.quoteNumber);
  }

  const calc = useMemo(() => calculateEtf({
    mrr,
    term,
    contractStart: contractStart ? new Date(contractStart) : null,
    terminationDate: terminationDate ? new Date(terminationDate) : null,
  }), [mrr, term, contractStart, terminationDate]);

  const canCalculate = mrr && term && contractStart && terminationDate && calc.warnings.length === 0;

  function handleGenerateLetter() {
    const html = buildEtfLetterHTML({
      clientName, clientAddress,
      mrr: Number(mrr), term: parseInt(term, 10),
      contractStart: new Date(contractStart),
      terminationDate: new Date(terminationDate),
      ferrumInfo,
      signatureMode: 'none',
      contractReference,
    });
    const w = window.open('', '_blank');
    if (!w) {
      window.alert('Could not open the letter — your browser may be blocking pop-ups. Allow pop-ups and try again.');
      return;
    }
    w.document.write(html);
    w.document.close();
  }

  async function handleDownloadPdf() {
    setGeneratingPdf(true);
    setPdfStatus('');
    try {
      const letterId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const letterRef = `ETF-${letterId.slice(0, 8)}`;
      const html = buildEtfLetterHTML({
        clientName, clientAddress,
        mrr: Number(mrr), term: parseInt(term, 10),
        contractStart: new Date(contractStart),
        terminationDate: new Date(terminationDate),
        ferrumInfo,
        signatureMode: 'none',
        contractReference,
        letterRef,
      });
      const safeBiz = (clientName || 'Client').replace(/[^a-z0-9]+/gi, '_').slice(0, 40);
      const filename = `ETF_Letter_${safeBiz}_${letterRef}.pdf`;
      const res = await fetch('/.netlify/functions/etfDocument', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'render_pdf',
          payload: {
            html,
            filename,
            letterId,                                                    // v7.19
            entityName: clientName,
            etfAmount: calc.etfAmount,
            contractReference,
            hubDealId: hubSelection?.dealId || null,
            hubCompanyId: hubSelection?.companyId || null,
            hubCompanyName: hubSelection?.companyName || null,
            quoteId: carriedQuoteId || null,                             // v7.19
            supersededFrom: supersededFrom || null,                      // v7.19
            // v7.19: full metadata so etf_letters columns aren't NULL
            mrr: Number(mrr),
            termMonths: parseInt(term, 10),
            contractStartDate: contractStart || null,
            terminationDate: terminationDate || null,
            monthsElapsed: calc.monthsElapsed,
            monthsRemaining: calc.monthsRemaining,
            clientAddress: clientAddress || null,
            clientContactName: hubSelection?.contactName || null,
            clientContactEmail: hubSelection?.contactEmail || null,
            calculatedBy: profile?.full_name || profile?.email || null,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.pdf_base64) {
        throw new Error(data?.detail || data?.error || `HTTP ${res.status}`);
      }
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${data.pdf_base64}`;
      link.download = data.filename || filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // v7.15: surface HubSpot upload status in the inline status line
      if (data.hubspot?.ok) {
        setPdfStatus(`✓ PDF downloaded (${letterRef}) · Uploaded to HubSpot deal (file ${String(data.hubspot.fileId).slice(0, 8)}…)`);
      } else if (data.hubspot && !data.hubspot.ok) {
        setPdfStatus(`✓ PDF downloaded (${letterRef}) · ⚠ HubSpot upload failed: ${data.hubspot.error || 'unknown error'}`);
      } else {
        setPdfStatus(`✓ PDF downloaded (${letterRef})`);
      }
    } catch (e) {
      console.error('ETF PDF download failed:', e);
      setPdfStatus('✗ ' + (e.message || 'PDF failed'));
    }
    setGeneratingPdf(false);
  }

  return (
    <div style={{ height:'100%', flex:1, overflow:'auto', padding:'24px', background:v3.color.bg, fontFamily:v3.font.family }}>
      <div style={{ maxWidth:780, margin:'0 auto' }}>
        <div style={{ marginBottom:16 }}>
          <h1 style={{ fontSize:v3.font.size.pageTitle, fontWeight:v3.font.weight.semibold, color:v3.color.body, margin:0, marginBottom:4, letterSpacing:v3.font.letterSpacing.tight, display:'flex', alignItems:'center', gap:8 }}>
            <Icon name="calculator" size={22} /> Early Termination Fee Calculator
          </h1>
          <div style={{ fontSize:v3.font.size.label, color:v3.color.muted, lineHeight:1.5 }}>
            Standard MSA formula: <strong>ETF = MRR × months remaining</strong>. Select the HubSpot
            company and deal first so the generated PDF auto-uploads to the deal.
            For per-quote calculations with SignWell two-party send, open the saved quote and
            click the <strong>Calculate ETF</strong> button on the page.
          </div>
        </div>

        {/* v7.19: Banner when this calculator was opened from "Void & Create
            New" on the ETF Letters page. Tells the rep the new letter will
            be linked to the voided one. */}
        {prefillFromVoid && (
          <div style={{ marginBottom:14, padding:'10px 12px', background:v3.color.status.amberBg, border:`1px solid ${v3.color.status.amberText}33`, borderRadius:v3.radius.card, fontSize:v3.font.size.small, color:v3.color.status.amberText }}>
            <strong>Replacing voided letter</strong>
            {prefillFromVoid.contract_reference ? <> · {prefillFromVoid.contract_reference}</> : null}
            <span style={{ marginLeft:8, fontFamily:v3.font.mono, fontSize:v3.font.size.micro, color:v3.color.status.amberText }}>
              ETF-{String(prefillFromVoid.id || '').slice(0, 8)}
            </span>
            <div style={{ fontSize:v3.font.size.micro, color:v3.color.status.amberText, marginTop:3 }}>
              The new letter you generate here will be linked back to the voided original.
              Edit any fields below and click Download PDF when ready.
            </div>
          </div>
        )}

        {/* v7.15: HubSpot picker — drives the rest of the form.
            v7.19: skipped when prefilling from a voided letter — company &
            deal context is carried in via location.state and the rep doesn't
            need to re-pick. */}
        {!prefillFromVoid && (
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:v3.font.size.micro, fontWeight:v3.font.weight.semibold, color:v3.color.muted, textTransform:'uppercase', letterSpacing:v3.font.letterSpacing.uppercase, marginBottom:5 }}>
              HubSpot Company &amp; Deal
            </div>
            <HubSpotDealPicker
              value={hubSelection}
              onSelect={handleHubSelect}
            />
          </div>
        )}
        {prefillFromVoid && hubSelection && (
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:v3.font.size.micro, fontWeight:v3.font.weight.semibold, color:v3.color.muted, textTransform:'uppercase', letterSpacing:v3.font.letterSpacing.uppercase, marginBottom:5 }}>
              HubSpot Company &amp; Deal
            </div>
            <div style={{ padding:'10px 12px', background:v3.color.status.greenBg, border:`1px solid ${v3.color.status.greenText}33`, borderRadius:v3.radius.card, fontSize:v3.font.size.small }}>
              <div style={{ fontWeight:v3.font.weight.semibold, color:v3.color.body }}>
                {hubSelection.companyName || prefillFromVoid.client_name || 'Unknown'}
                {hubSelection.dealName && <span style={{ color:v3.color.muted, fontWeight:v3.font.weight.normal }}> · {hubSelection.dealName}</span>}
              </div>
              <div style={{ fontSize:v3.font.size.micro, color:v3.color.muted, marginTop:2 }}>
                Carried over from the voided letter — the new letter will attach to the same HubSpot deal.
              </div>
            </div>
          </div>
        )}

        <div style={{ background:v3.color.card, border:`1px solid ${v3.color.border}`, borderRadius:v3.radius.cardLarge, padding:'18px 20px' }}>
          <EtfCalculatorBody
            clientName={clientName} setClientName={setClientName}
            clientAddress={clientAddress} setClientAddress={setClientAddress}
            contractReference={contractReference} setContractReference={setContractReference}
            clientContact={''} setClientContact={() => {}}
            clientEmail={''}   setClientEmail={() => {}}
            mrr={mrr} setMrr={setMrr}
            term={term} setTerm={setTerm}
            contractStart={contractStart} setContractStart={setContractStart}
            terminationDate={terminationDate} setTerminationDate={setTerminationDate}
            calc={calc}
            canCalculate={canCalculate}
            onGenerateLetter={handleGenerateLetter}
            onPostToHubspot={null}
            onDownloadPdf={handleDownloadPdf}
            onSendViaSignwell={null}
            postingNote={false}
            postStatus=""
            generatingPdf={generatingPdf}
            pdfStatus={pdfStatus}
            sendingSignwell={false}
            signwellStatus=""
            showContactFields={false}
            hintMessage={null}
          />
        </div>
      </div>
    </div>
  );
}
